import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import SEO from "../SEO";
import NavaService from "../../services/NavaService";
import ContactImg1 from "../../assets/img/contact1.png";
import ContactImg2 from "../../assets/img/contact3.png";
import ContactImg3 from "../../assets/img/contact4.png";

import {
  FaHandshake as Handshake,
  FaMapMarkerAlt as MapPin,
  FaPhoneAlt as Phone,
  FaEnvelope as Mail,
} from "react-icons/fa";

export default function Contact({ standalone = true }) {
  const location = useLocation();
  const contactSubject = location.state?.contactSubject || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: contactSubject,
    message: "",
  });
  const [status, setStatus] = useState(null);
  const [isLarge, setIsLarge] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 992px)").matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 992px)");
    const handler = (e) => setIsLarge(e.matches);

    setIsLarge(mq.matches);

    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    if (!contactSubject) return;
    setForm((current) => ({ ...current, subject: contactSubject }));
  }, [contactSubject]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const isValid = Boolean(
    form.name.trim() && form.email.trim() && form.subject.trim() && form.message.trim()
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || status === "sending") return;

    setStatus("sending");
    try {
      await NavaService.postMessage({
        firstName: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });

      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const ContactForm = (
    <FormCard onSubmit={handleSubmit}>
      <FormRow>
        <Field>
          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />
        </Field>

        <Field>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
          />
        </Field>
      </FormRow>

      <Field>
        <label htmlFor="subject">Subject</label>
        <input
          type="text"
          id="subject"
          name="subject"
          value={form.subject}
          onChange={handleChange}
          required
        />
      </Field>

      <Field>
        <label htmlFor="message">Message</label>
        <textarea
          id="message"
          name="message"
          rows="5"
          value={form.message}
          onChange={handleChange}
          required
        />
      </Field>

      <SubmitButton type="submit" disabled={!isValid || status === "sending"}>
        {status === "sending" ? "Sending..." : "Send Message"}
      </SubmitButton>

      {status === "success" && <StatusText success>Message sent — thank you!</StatusText>}
      {status === "error" && <StatusText>Failed to send message. Please try again later.</StatusText>}
    </FormCard>
  );

  return (
    <>
      {/* <SEO
        title={
          standalone
            ? "Contact Sare Auctioneers"
            : "Let's Get in Touch"
        }
        description={
          standalone
            ? "Contact Sare Auctioneers at View Park Towers, Nairobi, for professional auctioneering, property auctions, debt collection, repossessions and related services."
            : "Get in touch with Sare Auctioneers about property auctions, auctioneering services and related services."
        }
      /> */}
      {standalone && (
        <SEO
          title="Contact Sare Auctioneers"
          description="Contact Sare Auctioneers at View Park Towers, Nairobi, for professional auctioneering, property auctions, debt collection, repossessions and related services."
        />
      )}

      {standalone ? (
        <StandaloneContact>
          <div className="container">

            {/* =========================================
                HERO
            ========================================= */}
            <Hero>
              <HeroText>
                <p className="eyebrow">GET IN TOUCH</p>

                <h1>
                  Let's get in touch
                </h1>

                <p className="heroDescription">
                  We'd love to hear from you. Whether you're interested in a
                  listed property, need help with the auction process, or want
                  to consign land for auction, our team is ready to help.
                </p>
              </HeroText>

              <HeroIcon>
                <Handshake size={68} />
              </HeroIcon>
            </Hero>

            {/* =========================================
                LOCATION + MAP
            ========================================= */}
            <ContactLayout>

              {/* LEFT SIDE */}
              <ContactInformation>
                <p className="eyebrow">
                  OUR LOCATION
                </p>

                <h2>
                  Visit Our Offices
                </h2>

                <ContactCards>

                  {/* PHYSICAL LOCATION */}
                  <ContactCard>
                    <ContactIcon>
                      <MapPin size={23} />
                    </ContactIcon>

                    <ContactCardContent>
                      <label>
                        Physical Location
                      </label>

                      <p>
                        View Park Towers, 16th Floor
                        <br />
                        Wing A, Utalii Street
                        <br />
                        Nairobi, Kenya
                      </p>
                    </ContactCardContent>
                  </ContactCard>

                  {/* POSTAL ADDRESS */}
                  <ContactCard>
                    <ContactIcon>
                      <Mail size={23} />
                    </ContactIcon>

                    <ContactCardContent>
                      <label>
                        Postal Address
                      </label>

                      <p>
                        P. O. Box 12962-00400
                        <br />
                        Nairobi, Kenya
                      </p>
                    </ContactCardContent>
                  </ContactCard>

                </ContactCards>
              </ContactInformation>


              {/* RIGHT SIDE - MAP */}
              <MapSection>

                <MapHeader>
                  <div>
                    <p className="eyebrow">
                      FIND US
                    </p>

                    <h2>
                      Our Office Location
                    </h2>
                  </div>

                  <LocationBadge>
                    <MapPin size={16} />

                    Nairobi
                  </LocationBadge>
                </MapHeader>

                <MapWrapper>
                  <iframe
                    title="Sare Auctioneers - View Park Towers"
                    src="https://www.google.com/maps?q=View+Park+Towers,+Utalii+Street,+Nairobi,+Kenya&output=embed"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </MapWrapper>

                <MapAddress>
                  <MapPin size={18} />

                  <span>
                    View Park Towers, 16th Floor, Wing A,
                    Utalii Street, Nairobi, Kenya
                  </span>
                </MapAddress>

              </MapSection>

            </ContactLayout>


            {/* =========================================
                PHONE + EMAIL
            ========================================= */}
            <ChannelsSection>

              <ChannelCard>
                <ChannelIcon>
                  <Phone size={25} />
                </ChannelIcon>

                <ChannelContent>
                  <label>
                    Telephone
                  </label>

                  <a href="tel:+254722922095">
                    +254 722 922 095
                  </a>

                  <a href="tel:+254722673571">
                    +254 722 673 571
                  </a>
                </ChannelContent>
              </ChannelCard>


              <ChannelCard>
                <ChannelIcon>
                  <Mail size={25} />
                </ChannelIcon>

                <ChannelContent>
                  <label>
                    Email
                  </label>

                  <a href="mailto:sareauctioneers.co@gmail.com">
                    sareauctioneers.co@gmail.com
                  </a>
                </ChannelContent>
              </ChannelCard>

            </ChannelsSection>


            {/* =========================================
                CONTACT FORM
            ========================================= */}
            <FormSection>
              <div>
                <p className="eyebrow">
                  SARE AUCTIONEERS
                </p>

                <h2>
                  We are ready to assist you.
                </h2>

                <p className="intro">
                  Get in touch with our team to discuss your
                  requirements and find out how we can assist you.
                </p>
              </div>

              {ContactForm}
            </FormSection>

          </div>
        </StandaloneContact>
      ) : (
        /* =========================================
           NON-STANDALONE - FORM + IMAGES (landing only)
        ========================================= */
        <CompactContact id="contact">
          <div className="container">
            <CompactRow>
              <FormCol>{ContactForm}</FormCol>

              <ImagesCol>
                <LeftStack>
                  <ContactImgBox>
                    <img src={ContactImg1} alt="Sare Auctioneers office" />
                  </ContactImgBox>
                  <ContactImgBox>
                    <img src={ContactImg2} alt="Sare Auctioneers office" />
                  </ContactImgBox>
                </LeftStack>

                {isLarge && (
                  <RightImgWrapper>
                    <MainImgWrapper>
                      <img src={ContactImg3} alt="Sare Auctioneers office" />
                    </MainImgWrapper>
                  </RightImgWrapper>
                )}
              </ImagesCol>
            </CompactRow>
          </div>
        </CompactContact>
      )}

    </>
  );
}


/* =====================================================
   STANDALONE PAGE
===================================================== */

const StandaloneContact = styled.main`
  min-height: 70vh;

  padding: 150px 0 100px;

  color: #3b1718;

  .eyebrow {
    margin: 0 0 10px;

    color: #8a3638;

    font-size: 12px;
    font-weight: 700;

    letter-spacing: 2px;
    text-transform: uppercase;
  }

  @media (max-width: 768px) {
    padding: 120px 0 70px;
  }
`;


/* =====================================================
   HERO
===================================================== */

const Hero = styled.section`
  display: grid;

  grid-template-columns: 1fr 190px;

  gap: 60px;

  align-items: center;

  padding: 45px 0 80px;

  border-bottom: 1px solid #eadede;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;

    gap: 35px;

    padding: 20px 0 55px;
  }
`;


const HeroText = styled.div`
  h1 {
    margin: 0 0 22px;

    color: #3b1718;

    font-size: 52px;

    line-height: 1.08;

    font-weight: 800;
  }

  .heroDescription {
    max-width: 720px;

    margin: 0;

    color: #6b4a4a;

    font-size: 17px;

    line-height: 1.8;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 40px;
    }

    .heroDescription {
      font-size: 15px;
    }
  }
`;


const HeroIcon = styled.div`
  width: 160px;
  height: 160px;

  display: flex;

  align-items: center;
  justify-content: center;

  justify-self: center;

  background: #3b1718;

  color: #ffffff;

  border-radius: 50%;

  box-shadow:
    0 20px 45px rgba(59, 23, 24, 0.15);

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;


/* =====================================================
   LOCATION / MAP LAYOUT
===================================================== */

const ContactLayout = styled.section`
  display: grid;

  grid-template-columns: 0.85fr 1.15fr;

  gap: 65px;

  align-items: start;

  padding: 85px 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;

    gap: 50px;

    padding: 65px 0;
  }
`;


/* =====================================================
   CONTACT INFORMATION
===================================================== */

const ContactInformation = styled.div`
  h2 {
    margin: 0 0 28px;

    color: #3b1718;

    font-size: 36px;

    line-height: 1.2;
  }
`;


const ContactCards = styled.div`
  display: flex;

  flex-direction: column;
`;


const ContactCard = styled.div`
  display: flex;

  align-items: flex-start;

  gap: 18px;

  padding: 22px 0;

  border-bottom: 1px solid #eadede;

  &:last-child {
    border-bottom: none;
  }
`;


const ContactIcon = styled.div`
  flex-shrink: 0;

  width: 50px;
  height: 50px;

  display: flex;

  align-items: center;
  justify-content: center;

  background: #f3e4e4;

  color: #8a3638;

  border-radius: 9px;
`;


const ContactCardContent = styled.div`
  label {
    display: block;

    margin-bottom: 7px;

    color: #8a3638;

    font-size: 11px;

    font-weight: 700;

    letter-spacing: 1.2px;

    text-transform: uppercase;
  }

  p {
    margin: 0;

    color: #3b1718;

    font-size: 16px;

    line-height: 1.7;

    font-weight: 600;
  }
`;


/* =====================================================
   MAP
===================================================== */

const MapSection = styled.div`
  padding: 25px;

  background: #f8f6f5;

  border-radius: 14px;

  box-shadow:
    0 15px 40px rgba(59, 23, 24, 0.06);
`;


const MapHeader = styled.div`
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 20px;

  margin-bottom: 20px;

  h2 {
    margin: 0;

    color: #3b1718;

    font-size: 28px;

    line-height: 1.2;
  }

  @media (max-width: 600px) {
    align-items: flex-start;

    flex-direction: column;
  }
`;


const LocationBadge = styled.div`
  display: flex;

  align-items: center;

  gap: 7px;

  padding: 8px 13px;

  background: #f3e4e4;

  color: #8a3638;

  border-radius: 20px;

  font-size: 12px;

  font-weight: 700;
`;


const MapWrapper = styled.div`
  width: 100%;

  height: 450px;

  overflow: hidden;

  border-radius: 10px;

  background: #ddd;

  iframe {
    display: block;

    width: 100%;
    height: 100%;

    border: 0;
  }

  @media (max-width: 600px) {
    height: 350px;
  }
`;


const MapAddress = styled.div`
  display: flex;

  align-items: flex-start;

  gap: 9px;

  margin-top: 17px;

  color: #5d191a;

  font-size: 13px;

  line-height: 1.6;

  svg {
    flex-shrink: 0;

    margin-top: 2px;

    color: #8a3638;
  }
`;


/* =====================================================
   PHONE + EMAIL
===================================================== */

const ChannelsSection = styled.section`
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 20px;

  margin-bottom: 85px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;


const ChannelCard = styled.div`
  display: flex;

  align-items: center;

  gap: 20px;

  min-height: 125px;

  padding: 27px;

  background: #3b1718;

  border-radius: 11px;

  box-shadow:
    0 15px 35px rgba(59, 23, 24, 0.10);
`;


const ChannelIcon = styled.div`
  flex-shrink: 0;

  width: 52px;
  height: 52px;

  display: flex;

  align-items: center;
  justify-content: center;

  background: #8a3638;

  color: #ffffff;

  border-radius: 9px;
`;


const ChannelContent = styled.div`
  label {
    display: block;

    margin-bottom: 8px;

    color: #d9a7a7;

    font-size: 11px;

    font-weight: 700;

    letter-spacing: 1.2px;

    text-transform: uppercase;
  }

  a {
    display: block;

    margin-bottom: 4px;

    color: #ffffff;

    font-size: 15px;

    font-weight: 600;

    line-height: 1.5;

    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;


/* =====================================================
   CONTACT FORM SECTION (standalone)
===================================================== */

const FormSection = styled.section`
  padding: 55px;

  background: #f8f6f5;

  border-radius: 14px;

  h2 {
    margin: 0 0 12px;

    color: #3b1718;

    font-size: 32px;
  }

  .intro {
    max-width: 600px;

    margin: 0 0 35px;

    color: #6b4a4a;

    line-height: 1.7;
  }

  @media (max-width: 800px) {
    padding: 40px 25px;
  }
`;


/* =====================================================
   CONTACT FORM (shared between standalone + compact)
===================================================== */

const FormCard = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;

  max-width: 720px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 8px;
    color: #3b1718;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }

  input,
  textarea {
    width: 100%;
    background: #ffffff;
    border: 1px solid #eadede;
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 15px;
    color: #3b1718;
    outline: none;
    font-family: inherit;
    box-sizing: border-box;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: #8a3638;
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
`;

const SubmitButton = styled.button`
  align-self: flex-start;

  padding: 13px 28px;

  background: #8a3638;
  color: #ffffff;

  border: 0;
  border-radius: 7px;

  font-size: 14px;
  font-weight: 700;

  cursor: pointer;

  transition: background 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background: #3b1718;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) => (p.success ? "#2e7d32" : "#b3261e")};
`;


/* =====================================================
   NON-STANDALONE / COMPACT CONTACT - FORM ONLY
===================================================== */

const CompactContact = styled.main`
  min-height: 50vh;

  padding: 80px 0;

  color: #3b1718;
`;

const CompactRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 50px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const FormCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const ImagesCol = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex: 1;

  @media (max-width: 900px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LeftStack = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 600px) {
    width: 100%;
    align-items: center;
  }
`;

const RightImgWrapper = styled.div`
  width: 50%;
  display: flex;
  align-items: flex-start;
  margin-top: 60px;

  @media (max-width: 600px) {
    width: 100%;
    margin-top: 20px;
    justify-content: center;
  }
`;

const ContactImgBox = styled.div`
  max-width: 180px;
  align-self: flex-end;
  margin: 10px 30px 10px 0;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 600px) {
    align-self: center;
    margin: 10px auto;
    max-width: 220px;
    width: 90%;
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const MainImgWrapper = styled.div`
  width: 100%;
  height: 380px;
  overflow: hidden;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;